// TRUST-REIMBURSEMENTS-4 (2026-06-03) — Operations → Reimbursement Intake.
// A confidence-scored review queue for reconstructing reimbursement records from
// QBO deposits/checks (source of truth) + legitimate VCA-only items. NOTHING
// auto-commits — every candidate is human-reviewed here. Gated server-side by
// manage_reimbursement_intake; commit re-checks the claim wall + approve cap.
import { useState, useEffect, useCallback } from "react";
import { apiFetch, fmt } from "../api";
import { Spinner, StatusPill } from "./ui";  // UI-SWEEP-ACCOUNTING
import { AllocationEditor, ALLOC_MODE_LABEL, TYPE_LABEL } from "./Reimbursements";

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const sumAlloc = (rows) => round2((rows || []).reduce((a, r) => a + (Number(r.allocated_amount) || 0), 0));

const TIER = {
  auto:      { bg: "#dcfce7", c: "#166534", label: "Auto" },
  suggested: { bg: "#fef3c7", c: "#92400e", label: "Suggested" },
  review:    { bg: "#fee2e2", c: "#991b1b", label: "Review" },
};
const STATUS_STYLE = {
  unmatched: { bg: "#fee2e2", c: "#991b1b" }, suggested: { bg: "#fef3c7", c: "#92400e" },
  ready: { bg: "#dbeafe", c: "#1e40af" }, committed: { bg: "#dcfce7", c: "#166534" },
  ignored: { bg: "#e2e8f0", c: "#475569" }, approved: { bg: "#dbeafe", c: "#1e40af" }, error: { bg: "#fee2e2", c: "#991b1b" },
};
// Filter tab → match_status value (Needs Review == unmatched).
const FILTERS = [["all", "All"], ["unmatched", "Needs Review"], ["suggested", "Suggested"],
  ["ready", "Ready"], ["committed", "Committed"], ["ignored", "Ignored"]];

function Conf({ score, tier }) {
  const t = TIER[tier] || TIER.review;
  return (
    <span title={`Confidence ${score == null ? "—" : score}`} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <span style={{ fontFamily: "JetBrains Mono,monospace", fontWeight: 700, fontSize: 13 }}>{score == null ? "—" : score}</span>
      <span className="badge" style={{ background: t.bg, color: t.c, fontSize: 9, padding: "1px 6px" }}>{t.label}</span>
    </span>
  );
}
function StatusChip({ s }) {
  const label = s === "unmatched" ? "Needs Review" : s.charAt(0).toUpperCase() + s.slice(1);
  const tone = { matched: "green", committed: "green", ignored: "slate", staged: "blue" }[s];
  return <StatusPill sm status={label} tone={tone}>{label}</StatusPill>;
}

// QBO connection + sync bar. Shows per-company config/connection state and lets
// an intake user pull deposits → candidates once creds are seeded in secrets.env.
function QboBar({ onSynced }) {
  const [h, setH] = useState(null);
  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState(null);
  const load = useCallback(() => { apiFetch("/qbo/health").then(setH).catch(() => setH(null)); }, []);
  useEffect(() => { load(); }, [load]);
  if (!h) return null;
  const sync = (company) => {
    setBusy(company + ":sync"); setMsg(null);
    apiFetch("/qbo/sync", { method: "POST", body: { company } })
      .then(r => { setMsg(`${company}: pulled ${r.staged_total}, ${r.candidates_created} new candidate(s).`); onSynced && onSynced(); load(); })
      .catch(e => setMsg(`${company}: ${String(e.message || e)}`)).finally(() => setBusy(null));
  };
  const test = (company) => {
    setBusy(company + ":test"); setMsg(null);
    apiFetch("/qbo/connection-test", { method: "POST", body: { company } })
      .then(r => setMsg(r.ok ? `${company}: connected${r.qbo_company_name ? " — " + r.qbo_company_name : ""}.` : `${company}: ${r.error}`))
      .catch(e => setMsg(`${company}: ${String(e.message || e)}`)).finally(() => { setBusy(null); load(); });
  };
  return (
    <div className="card" style={{ marginBottom: 12 }}>
      <div className="card-body" style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap", padding: "10px 16px" }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: "var(--text2)" }}>QBO {h.client_configured ? `(${h.environment})` : ""}</span>
        {!h.client_configured && <span style={{ fontSize: 12, color: "var(--text3)" }}>Not connected — add QBO credentials to secrets.env (see plans/QBO_CREDENTIAL_SETUP.md).</span>}
        {["trust", "operating"].map(c => {
          const cc = (h.companies || {})[c] || {};
          const ok = cc.configured;
          return (
            <span key={c} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12 }}>
              <span style={{ width: 8, height: 8, borderRadius: 8, background: ok ? "#16a34a" : "#cbd5e1" }} />
              <span style={{ fontWeight: 600, textTransform: "capitalize" }}>{c}</span>
              {cc.last_sync_at && <span style={{ color: "var(--text3)" }}>· synced {fmt.date(cc.last_sync_at)}</span>}
              {ok && <button className="btn btn-outline btn-sm" style={{ padding: "1px 8px", fontSize: 11 }} disabled={!!busy} onClick={() => test(c)}>{busy === c + ":test" ? "…" : "Test"}</button>}
              {ok && <button className="btn btn-blue btn-sm" style={{ padding: "1px 8px", fontSize: 11 }} disabled={!!busy} onClick={() => sync(c)}>{busy === c + ":sync" ? "Syncing…" : "Sync"}</button>}
            </span>
          );
        })}
        {msg && <span style={{ fontSize: 12, color: "var(--text2)" }}>{msg}</span>}
      </div>
    </div>
  );
}

export function ReimbursementIntakeQueue({ onNavigateToClaim }) {
  const [status, setStatus] = useState("all");
  const [qbo, setQbo] = useState("");           // "" | "true" | "false"
  const [data, setData] = useState({ candidates: [], summary: {}, total: 0 });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [selId, setSelId] = useState(null);
  const [showStage, setShowStage] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (status !== "all") qs.set("status", status);
    if (qbo) qs.set("qbo", qbo);
    apiFetch(`/intake/candidates?${qs.toString()}`)
      .then(d => { setData(d || { candidates: [] }); setErr(null); })
      .catch(e => setErr(String(e.message || e)))
      .finally(() => setLoading(false));
  }, [status, qbo]);
  useEffect(() => { load(); }, [load]);

  const bulkCommit = () => {
    if (!window.confirm("Commit all high-confidence ‘Ready’ candidates into funds request records?")) return;
    setBusy(true);
    apiFetch(`/intake/candidates/commit-batch`, { method: "POST", body: {} })
      .then(r => { window.alert(`Committed ${r.committed_count}. Skipped ${r.skipped?.length || 0}.`); load(); })
      .catch(e => setErr(String(e.message || e)))
      .finally(() => setBusy(false));
  };

  const sm = data.summary || {};
  const th = { padding: "8px 10px", textAlign: "left", fontSize: 11, color: "var(--text3)", textTransform: "uppercase" };
  const td = { padding: "8px 10px", fontSize: 13 };
  return (
    <div>
      <QboBar onSynced={load} />
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="card-body" style={{ display: "flex", gap: 18, flexWrap: "wrap", alignItems: "center", padding: "12px 18px" }}>
          <div><div style={{ fontSize: 11, color: "var(--text3)", textTransform: "uppercase" }}>Needs Review</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#991b1b" }}>{sm.unmatched || 0}</div></div>
          <div><div style={{ fontSize: 11, color: "var(--text3)", textTransform: "uppercase" }}>Suggested</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#92400e" }}>{sm.suggested || 0}</div></div>
          <div><div style={{ fontSize: 11, color: "var(--text3)", textTransform: "uppercase" }}>Ready</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#1e40af" }}>{sm.ready || 0}</div></div>
          <div><div style={{ fontSize: 11, color: "var(--text3)", textTransform: "uppercase" }}>Committed</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#166534" }}>{sm.committed || 0}</div></div>
          <div style={{ flex: 1 }} />
          <select value={qbo} onChange={e => setQbo(e.target.value)} style={{ padding: "6px 9px", fontSize: 12, border: "1px solid var(--border)", borderRadius: 6 }}>
            <option value="">All sources</option><option value="true">QBO only</option><option value="false">VCA-only</option>
          </select>
          {(sm.ready || 0) > 0 && <button className="btn btn-blue btn-sm" onClick={bulkCommit} disabled={busy}>Commit Ready ({sm.ready})</button>}
          <button className="btn btn-outline btn-sm" onClick={() => { setShowStage(true); setSelId(null); }}>+ Stage Candidate</button>
        </div>
      </div>

      <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
        {FILTERS.map(([k, label]) => (
          <button key={k} onClick={() => setStatus(k)}
            style={{ padding: "5px 12px", fontSize: 12, borderRadius: 16, cursor: "pointer", fontWeight: 600,
                     border: "1px solid " + (status === k ? "var(--navy)" : "var(--border)"),
                     background: status === k ? "var(--navy)" : "transparent", color: status === k ? "#fff" : "var(--text2)" }}>
            {label}{k !== "all" && sm[k] != null ? ` ${sm[k]}` : ""}
          </button>
        ))}
      </div>

      {err && <div className="card" style={{ marginBottom: 12 }}><div className="card-body" style={{ color: "var(--red)", fontSize: 13 }}>{err}</div></div>}
      {showStage && <StageForm onClose={() => setShowStage(false)} onSaved={(c) => { setShowStage(false); load(); setSelId(c?.id || null); }} />}

      <div className="card">
        <div className="section-header"><span className="section-title">Candidates ({data.total || 0})</span></div>
        <div className="card-body" style={{ padding: 0 }}>
          {loading ? <Spinner label="Loading candidates…" />
            : (data.candidates || []).length === 0 ? <div className="empty-state" style={{ padding: 24 }}>No candidates in this view. Use “+ Stage Candidate” to add one, or connect QBO to populate the queue.</div>
            : (
            <table style={{ width: "100%", fontSize: 13 }}>
              <thead><tr style={{ textAlign: "left" }}>
                <th style={th}>Conf.</th><th style={th}>Claim</th><th style={th}>Payor</th>
                <th style={{ ...th, textAlign: "right" }}>Amount</th><th style={th}>Date</th>
                <th style={th}>Why / flags</th><th style={th}>Status</th><th style={th}></th>
              </tr></thead>
              <tbody>
                {data.candidates.map(c => (
                  <tr key={c.id} style={{ borderTop: "1px solid var(--border)", background: selId === c.id ? "var(--bg2,#f8fafc)" : "transparent" }}>
                    <td style={td}><Conf score={c.confidence_score} tier={c.confidence_tier} /></td>
                    <td style={td}>{c.matched_claim_number || <span style={{ color: "var(--text3)" }}>— unmatched —</span>}{!c.is_qbo && <span className="badge" style={{ marginLeft: 6, fontSize: 9, background: "#ede9fe", color: "#6d28d9" }}>VCA</span>}</td>
                    <td style={{ ...td, maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.payor_name || "—"}</td>
                    <td style={{ ...td, textAlign: "right", fontFamily: "JetBrains Mono,monospace" }}>{fmt.money(c.amount)}</td>
                    <td style={{ ...td, color: "var(--text3)", whiteSpace: "nowrap" }}>{c.transaction_date ? fmt.date(c.transaction_date) : "—"}</td>
                    <td style={{ ...td, maxWidth: 240 }}>
                      {(c.match_reasons || []).slice(0, 1).map((r, i) => <div key={i} style={{ fontSize: 11, color: "#166534" }}>✓ {r}</div>)}
                      {(c.issue_flags || []).slice(0, 2).map((f, i) => <div key={i} style={{ fontSize: 11, color: "#b45309" }}>⚠ {f}</div>)}
                    </td>
                    <td style={td}><StatusChip s={c.match_status} /></td>
                    <td style={{ ...td, textAlign: "right" }}><button className="btn btn-outline btn-sm" style={{ padding: "3px 10px", fontSize: 11 }} onClick={() => { setSelId(c.id); setShowStage(false); }}>Review</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {selId && <CandidateDrawer candId={selId} onChanged={load} onClose={() => setSelId(null)} onNavigateToClaim={onNavigateToClaim} />}
    </div>
  );
}

// ── Stage a manual / synthetic candidate ─────────────────────────────────────
function StageForm({ onClose, onSaved }) {
  const [f, setF] = useState({ source_system: "qbo", amount: "", payor_name: "", memo: "",
    reference_number: "", transaction_date: "", direction: "deposit" });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const set = (k, v) => setF(s => ({ ...s, [k]: v }));
  const save = () => {
    const amt = parseFloat(f.amount);
    if (!amt) { setErr("Enter an amount."); return; }
    setSaving(true); setErr(null);
    apiFetch(`/intake/candidates`, { method: "POST", body: {
      source_system: f.source_system, amount: amt, payor_name: f.payor_name, memo: f.memo,
      reference_number: f.reference_number, transaction_date: f.transaction_date || null,
      direction: f.direction, is_qbo: f.source_system !== "vca" } })
      .then(c => onSaved(c)).catch(e => setErr(String(e.message || e))).finally(() => setSaving(false));
  };
  const lbl = { fontSize: 12, color: "var(--text2)", fontWeight: 600, display: "block", marginBottom: 4 };
  const inp = { width: "100%", padding: "7px 10px", fontSize: 13, border: "1px solid var(--border)", borderRadius: 6 };
  return (
    <div className="card" style={{ marginBottom: 14, borderColor: "var(--navy)" }}>
      <div className="section-header"><span className="section-title">Stage Candidate (manual / test)</span></div>
      <div className="card-body" style={{ display: "grid", gap: 10 }}>
        <div className="grid-2" style={{ gap: 10 }}>
          <div><label style={lbl}>Source</label>
            <select style={inp} value={f.source_system} onChange={e => set("source_system", e.target.value)}>
              <option value="qbo">QBO</option><option value="vca">VCA-only (non-QBO)</option><option value="manual">Manual</option>
            </select></div>
          <div><label style={lbl}>Amount</label><input style={inp} type="number" step="0.01" value={f.amount} onChange={e => set("amount", e.target.value)} placeholder="0.00" /></div>
        </div>
        <div className="grid-2" style={{ gap: 10 }}>
          <div><label style={lbl}>Payor / underwriter</label><input style={inp} value={f.payor_name} onChange={e => set("payor_name", e.target.value)} /></div>
          <div><label style={lbl}>Transaction date</label><input style={inp} type="date" value={f.transaction_date} onChange={e => set("transaction_date", e.target.value)} /></div>
        </div>
        <div><label style={lbl}>Memo / description (include claim # if known)</label><input style={inp} value={f.memo} onChange={e => set("memo", e.target.value)} placeholder="e.g. Deposit re claim 26-002800EV funds request" /></div>
        <div className="grid-2" style={{ gap: 10 }}>
          <div><label style={lbl}>Reference (check / deposit #)</label><input style={inp} value={f.reference_number} onChange={e => set("reference_number", e.target.value)} /></div>
          <div><label style={lbl}>Direction</label>
            <select style={inp} value={f.direction} onChange={e => set("direction", e.target.value)}>
              <option value="deposit">Deposit (money in)</option><option value="check">Check (money out)</option><option value="other">Other</option>
            </select></div>
        </div>
        {err && <div style={{ color: "var(--red)", fontSize: 12 }}>{err}</div>}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button className="btn btn-outline btn-sm" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>{saving ? "Staging…" : "Stage + Match"}</button>
        </div>
      </div>
    </div>
  );
}

// ── Candidate review / edit drawer ───────────────────────────────────────────
function CandidateDrawer({ candId, onChanged, onClose, onNavigateToClaim }) {
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [edit, setEdit] = useState({});           // staged edits
  const [rows, setRows] = useState([]);
  const [underwriters, setUnderwriters] = useState([]);
  const [claimQ, setClaimQ] = useState("");
  const [claimHits, setClaimHits] = useState([]);

  const load = useCallback(() => {
    setLoading(true);
    apiFetch(`/intake/candidates/${candId}`)
      .then(d => { setC(d); setEdit({}); setRows((d.recipients || []).map(r => ({ ...r }))); setErr(null); })
      .catch(e => setErr(String(e.message || e))).finally(() => setLoading(false));
  }, [candId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const cid = (edit.matched_claim_id != null ? edit.matched_claim_id : c?.matched_claim_id);
    if (cid && cid !== -1) apiFetch(`/claims/${cid}/slip-underwriters`).then(d => setUnderwriters(d.underwriters || [])).catch(() => setUnderwriters([]));
    else setUnderwriters([]);
  }, [c?.matched_claim_id, edit.matched_claim_id]);

  const lookupClaim = (q) => {
    setClaimQ(q);
    if (!q || q.length < 2) { setClaimHits([]); return; }
    apiFetch(`/intake/claim-lookup?q=${encodeURIComponent(q)}`).then(d => setClaimHits(d.claims || [])).catch(() => setClaimHits([]));
  };

  const setE = (k, v) => setEdit(s => ({ ...s, [k]: v }));
  const val = (k) => (edit[k] !== undefined ? edit[k] : (c ? c[k] : undefined));
  const mode = val("allocation_mode") || "already_split";
  const amount = Number(val("amount")) || 0;

  const save = (extra) => {
    setBusy(true); setErr(null);
    const body = { ...edit, ...(extra || {}) };
    if (mode !== "auto_slip" && (edit.allocation_mode !== undefined || edit.recipients !== undefined || rows.length)) {
      body.recipients = mode === "already_split"
        ? [{ ...(rows[0] || { display_name: val("payor_name") }), allocated_amount: round2(amount) }]
        : rows.map(r => ({ ...r, allocated_amount: round2(r.allocated_amount) }));
    }
    return apiFetch(`/intake/candidates/${candId}`, { method: "PATCH", body })
      .then(() => { load(); onChanged && onChanged(); })
      .catch(e => { setErr(String(e.message || e)); throw e; })
      .finally(() => setBusy(false));
  };
  const act = (path) => { setBusy(true); setErr(null);
    apiFetch(`/intake/candidates/${candId}/${path}`, { method: "POST", body: {} })
      .then(() => { load(); onChanged && onChanged(); }).catch(e => setErr(String(e.message || e))).finally(() => setBusy(false)); };
  const commit = () => { save().then(() => act("commit")).catch(() => {}); };

  if (loading) return <div className="card" style={{ marginTop: 14 }}><Spinner label="Loading…" /></div>;
  if (!c) return null;
  const committed = c.match_status === "committed";
  const lbl = { fontSize: 12, color: "var(--text2)", fontWeight: 600, display: "block", marginBottom: 4 };
  const inp = { width: "100%", padding: "7px 10px", fontSize: 13, border: "1px solid var(--border)", borderRadius: 6 };

  return (
    <div className="card" style={{ marginTop: 14, borderColor: "var(--navy)" }}>
      <div className="section-header">
        <span className="section-title">Candidate · {fmt.money(c.amount)} · <Conf score={c.confidence_score} tier={c.confidence_tier} /> · <StatusChip s={c.match_status} /></span>
        <div style={{ display: "flex", gap: 6 }}>
          {!committed && <button className="btn btn-outline btn-sm" onClick={() => act("rematch")} disabled={busy} title="Re-run the matcher">Re-run Match</button>}
          {!committed && <button className="btn btn-outline btn-sm" onClick={() => save()} disabled={busy}>Save</button>}
          {!committed && <button className="btn btn-primary btn-sm" onClick={commit} disabled={busy} title="Create the funds request record(s)">Commit</button>}
          {!committed && <button className="btn btn-outline btn-sm" onClick={() => act("ignore")} disabled={busy} style={{ color: "#991b1b", borderColor: "#fecaca" }}>Ignore</button>}
          {committed && c.committed_request_id && onNavigateToClaim && c.matched_claim_id &&
            <button className="btn btn-blue btn-sm" onClick={() => onNavigateToClaim(c.matched_claim_id)}>Open Claim</button>}
          <button className="btn btn-outline btn-sm" onClick={onClose}>Close</button>
        </div>
      </div>
      <div className="card-body" style={{ display: "grid", gap: 12 }}>
        {err && <div style={{ color: "var(--red)", fontSize: 12 }}>{err}</div>}

        {/* Why it matched / issues */}
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ fontSize: 11, color: "var(--text3)", textTransform: "uppercase", marginBottom: 4 }}>Match reasons</div>
            {(c.match_reasons || []).length === 0 ? <div style={{ fontSize: 12, color: "var(--text3)" }}>—</div>
              : c.match_reasons.map((r, i) => <div key={i} style={{ fontSize: 12, color: "#166534" }}>✓ {r}</div>)}
          </div>
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ fontSize: 11, color: "var(--text3)", textTransform: "uppercase", marginBottom: 4 }}>Issue flags</div>
            {(c.issue_flags || []).length === 0 ? <div style={{ fontSize: 12, color: "#166534" }}>None</div>
              : c.issue_flags.map((f, i) => <div key={i} style={{ fontSize: 12, color: "#b45309" }}>⚠ {f}</div>)}
          </div>
        </div>
        <div style={{ fontSize: 12, color: "var(--text3)" }}>{c.memo || "—"}{c.reference_number ? ` · ref ${c.reference_number}` : ""}{c.source_ref ? ` · ${c.source_ref}` : ""}</div>

        {committed ? (
          <div style={{ fontSize: 13, color: "#166534", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 6, padding: "8px 10px" }}>
            Committed to claim {c.matched_claim_number} — request #{c.committed_request_id}{c.committed_receipt_id ? `, receipt #${c.committed_receipt_id}` : ""}{c.approved_by_name ? ` · by ${c.approved_by_name}` : ""}.
          </div>
        ) : (
          <>
            {/* Matched claim */}
            <div>
              <label style={lbl}>Matched claim</label>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{(val("matched_claim_id") && val("matched_claim_id") !== -1) ? (c.matched_claim_number || `#${val("matched_claim_id")}`) : "— none —"}</div>
                {val("matched_claim_id") && val("matched_claim_id") !== -1 && <button className="btn btn-outline btn-sm" style={{ padding: "1px 8px", fontSize: 11 }} onClick={() => setE("matched_claim_id", -1)}>clear</button>}
              </div>
              <input style={{ ...inp, marginTop: 6 }} value={claimQ} onChange={e => lookupClaim(e.target.value)} placeholder="Type a claim number to (re)assign…" />
              {claimHits.length > 0 && (
                <div style={{ border: "1px solid var(--border)", borderRadius: 6, marginTop: 4, maxHeight: 140, overflow: "auto" }}>
                  {claimHits.map(h => (
                    <div key={h.id} onClick={() => { setE("matched_claim_id", h.id); setClaimQ(""); setClaimHits([]); c.matched_claim_number = h.claim_number; }}
                      style={{ padding: "6px 10px", fontSize: 12, cursor: "pointer", borderTop: "1px solid var(--border)" }}>{h.claim_number}</div>
                  ))}
                </div>
              )}
            </div>

            <div className="grid-2" style={{ gap: 10 }}>
              <div><label style={lbl}>Request type</label>
                <select style={inp} value={val("request_type") || "manual_other"} onChange={e => setE("request_type", e.target.value)}>
                  {Object.entries(TYPE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select></div>
              <div><label style={lbl}>Payor / underwriter</label><input style={inp} value={val("payor_name") || ""} onChange={e => setE("payor_name", e.target.value)} /></div>
            </div>
            <div className="grid-2" style={{ gap: 10 }}>
              <div><label style={lbl}>Request amount</label><input style={inp} type="number" step="0.01" value={val("amount") ?? ""} onChange={e => setE("amount", e.target.value)} /></div>
              <div><label style={lbl}>Received amount</label><input style={inp} type="number" step="0.01" value={val("received_amount") ?? ""} onChange={e => setE("received_amount", e.target.value)} /></div>
            </div>
            <div className="grid-2" style={{ gap: 10 }}>
              <div><label style={lbl}>Received date</label><input style={inp} type="date" value={(val("received_date") || "").slice(0, 10)} onChange={e => setE("received_date", e.target.value)} /></div>
              <div><label style={lbl}>Basis / description</label><input style={inp} value={val("basis_description") || ""} onChange={e => setE("basis_description", e.target.value)} /></div>
            </div>

            {/* Allocation */}
            <div>
              <label style={lbl}>Allocation</label>
              <select style={inp} value={mode} onChange={e => setE("allocation_mode", e.target.value)}>
                {Object.entries(ALLOC_MODE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              <div style={{ fontSize: 11, color: "var(--text3)", margin: "4px 0 8px" }}>
                For a named QBO payor, “Already split” keeps the whole amount on that one underwriter — don’t re-split.
              </div>
              <AllocationEditor mode={mode} total={amount} underwriters={underwriters} rows={rows} setRows={setRows} />
            </div>

            <div><label style={lbl}>Resolution notes</label><input style={inp} value={val("resolution_notes") || ""} onChange={e => setE("resolution_notes", e.target.value)} placeholder="Why you resolved it this way (optional)" /></div>
          </>
        )}
      </div>
    </div>
  );
}
